import { useEffect, useRef, useState } from "react";
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { useParams, useSearch } from "@tanstack/react-router";
import { PLANS, type Plan } from "@backstory/protocol";
import { OrgLayout } from "../../components/Shell";
import { ErrorState, Skeleton } from "../../components/States";
import { AutoLoadMore, BusyButton, ConfirmDialog, DataTable, Meter as MeterBar, PageHeader, fmtNum, fmtUsd, useToast } from "../../components/ui";
import { formatDateTime } from "../../lib/format";
import { BillingUsage } from "./BillingCommon";
import { billingKeys } from "./api";
import { saasBillingApi } from "./api.saas";

const PLAN_ORDER: Plan[] = ["free", "team", "business", "enterprise"];

export function priceLabel(plan: Plan, interval: "month" | "year"): string {
  const p = PLANS[plan];
  if (plan === "enterprise") return "from $30K / year";
  if (p.priceMonthlyUsd === 0) return "$0";
  if (interval === "year") return `$${Math.round(p.priceMonthlyUsd * 0.85).toLocaleString()} / mo, billed yearly`;
  return `$${p.priceMonthlyUsd.toLocaleString()} / mo`;
}

export function paygoLabel(plan: Plan): string {
  const per1000 = PLANS[plan].sessionOveragePer1000Usd;
  if (per1000 == null) return "Hard cap at the package";
  const each = `${Number((per1000 / 10).toFixed(3))}¢ per extra session`;
  const terms = "invoiced monthly, due in 7 days";
  return plan === "free" ? `${each}, ${terms}, once a card is on file` : `${each} ($${per1000} per 1,000), ${terms}`;
}

export function BillingPage() {
  const { org: orgId } = useParams({ strict: false }) as { org: string };
  const search = useSearch({ strict: false }) as {
    plan?: Plan;
    checkout?: "success" | "canceled" | "ai-credits-success" | "ai-credits-canceled";
    amount?: string;
  };
  const toast = useToast();
  const state = useQuery({ queryKey: billingKeys.state(orgId), queryFn: () => saasBillingApi.state(orgId), retry: 0 });
  const payments = useInfiniteQuery({
    queryKey: billingKeys.payments(orgId),
    queryFn: ({ pageParam }) => saasBillingApi.payments(orgId, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor,
  });
  const paymentRows = payments.data?.pages.flatMap((page) => page.items) ?? [];
  const creditWallet = useQuery({ queryKey: billingKeys.aiCreditWallet(orgId), queryFn: () => saasBillingApi.aiCreditWallet(orgId), retry: 0 });
  const [interval, setInterval] = useState<"month" | "year">("month");
  const [cancelMode, setCancelMode] = useState<"period_end" | "immediate" | null>(null);
  const [paymentNotice, setPaymentNotice] = useState<PaymentNotice | null>(() => noticeForCheckout(search.checkout));
  const checkoutStarted = useRef(false);
  const checkout = useMutation({
    mutationFn: (plan: Plan) => saasBillingApi.checkout(orgId, plan, interval),
    onSuccess: (r) => window.location.assign(r.url),
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const portal = useMutation({
    mutationFn: () => saasBillingApi.portal(orgId),
    onSuccess: (r) => window.location.assign(r.url),
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const creditCheckout = useMutation({
    mutationFn: (amountUsd: 25 | 50 | 100) => saasBillingApi.aiCreditCheckout(orgId, amountUsd),
    onSuccess: (r) => window.location.assign(r.url),
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const cancel = useMutation({
    mutationFn: (immediate: boolean) => saasBillingApi.cancel(orgId, immediate),
    onSuccess: async () => {
      setCancelMode(null);
      await state.refetch();
      toast.push("success", "Subscription cancellation updated.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  useEffect(() => {
    if (checkoutStarted.current || checkout.isPending || !search.plan || search.plan === "free" || search.plan === "enterprise") return;
    checkoutStarted.current = true;
    checkout.mutate(search.plan);
  }, [search.plan]);
  useEffect(() => {
    if (!search.checkout) return;
    if (search.checkout === "ai-credits-success") {
      let attempts = 0;
      const id = window.setInterval(() => {
        attempts += 1;
        void creditWallet.refetch();
        if (attempts >= 10) window.clearInterval(id);
      }, 2_000);
      return () => window.clearInterval(id);
    }
    if (search.checkout !== "success") return;
    let attempts = 0;
    const id = window.setInterval(() => {
      attempts += 1;
      void state.refetch();
      void payments.refetch();
      if (attempts >= 10) window.clearInterval(id);
    }, 2_000);
    return () => window.clearInterval(id);
  }, [search.checkout]);
  const current = state.data?.plan ?? "free";

  return (
    <OrgLayout orgId={orgId}>
      <PageHeader
        title="Usage & billing"
        actions={state.data?.paymentMethodSet && state.data.portalSupported ? <button className="btn" onClick={() => portal.mutate()} disabled={portal.isPending}>Manage payment & invoices</button> : undefined}
      />
      {state.isPending ? (
        <Skeleton />
      ) : state.isError ? (
        <div className="card section"><ErrorState error={state.error} retry={() => void state.refetch()} /></div>
      ) : (
        <>
          {paymentNotice && (
            <PaymentNotice
              notice={paymentNotice}
              amount={Number(search.amount) || undefined}
              onClose={() => setPaymentNotice(null)}
            />
          )}
          {state.data.testMode && (
            <div className="banner warn" role="status">These charges are test charges. Cards are not billed.</div>
          )}
          {state.data.status === "trialing" && state.data.trialEndsAt && (
            <div className="banner info" role="status">Business trial active until {formatDateTime(state.data.trialEndsAt)}. Pick a plan below to keep your data after the trial.</div>
          )}
          {state.data.accessDisabled && state.data.accessDisabledReason === "payment" && (
            <div className="banner danger" role="alert">
              This account is paused until the invoice is paid. You can sign in to update the payment method. Recording is stopped.
            </div>
          )}
          {state.data.readOnly && !state.data.accessDisabled && (
            <div className="banner danger" role="alert">
              This account is read-only. Update the payment method to make changes again.
            </div>
          )}
          {state.data.accessEndsAt && !state.data.accessDisabled && (
            <div className="banner warn" role="status">
              Your subscription is canceled. Access ends <Countdown until={state.data.accessEndsAt} onElapsed={() => void state.refetch()} />. You can reactivate below.
            </div>
          )}
          {state.data.accessDisabled && state.data.accessDisabledReason !== "payment" && (
            <div className="banner danger" role="alert">
              Subscription access has ended. Recording is stopped. Billing and exports remain available.
            </div>
          )}
          <div className="row" style={{ justifyContent: "space-between", alignItems: "center", margin: "12px 0" }}>
            <div className="muted small">
              Current plan <span className="badge neutral">{current}</span>
              {state.data.currentPeriodEnd && <> · renews {formatDateTime(state.data.currentPeriodEnd)}</>}
              {" · "}payment method {state.data.paymentMethodSet ? "on file" : <strong>not set</strong>}
            </div>
            <div className="seg" role="radiogroup" aria-label="Billing interval">
              <button className="btn sm" aria-pressed={interval === "month"} onClick={() => setInterval("month")}>Monthly</button>
              <button className="btn sm" aria-pressed={interval === "year"} onClick={() => setInterval("year")}>Yearly · save 15%</button>
            </div>
          </div>
          <div className="plans" data-testid="plans">
            {PLAN_ORDER.map((plan) => {
              const p = PLANS[plan];
              const isCurrent = plan === current;
              const upgrade = PLAN_ORDER.indexOf(plan) > PLAN_ORDER.indexOf(current as Plan);
              return (
                <div key={plan} className={`card plan ${isCurrent ? "current" : ""}`} aria-current={isCurrent || undefined}>
                  <div className="plan-head"><h3>{plan.charAt(0).toUpperCase() + plan.slice(1)}</h3>{isCurrent && <span className="badge ok">Current</span>}</div>
                  <div className="price">{priceLabel(plan, interval)}</div>
                  <ul className="plan-facts">
                    <li>{fmtNum(p.storedSessionsIncluded)} stored sessions / mo</li>
                    <li>{fmtNum(p.customEventsIncluded)} custom events / mo{p.customEventOveragePerMillionUsd != null ? ` · $${p.customEventOveragePerMillionUsd} per extra million when allowed` : ""}</li>
                    <li>{state.data.usageBilling ? paygoLabel(plan) : "Hard cap at the package; no overage charges"}</li>
                    <li>{p.retentionDays}-day replay retention</li>
                    <li>{p.seats == null ? "Unlimited seats" : `${p.seats} seats`}</li>
                    <li>{fmtUsd(p.aiCreditsIncludedUsd)} AI credits / mo · AI pauses at zero</li>
                    <li>{p.canvasMinutesIncluded ? `${fmtNum(p.canvasMinutesIncluded)} visual min` : "No visual recording"}</li>
                    <li>{p.liveMinuteUsd != null
                      ? state.data.usageBilling
                        ? `${fmtNum(p.liveMinutesIncluded)} Live Assist participant-min / mo · then $${p.liveMinuteUsd}/min`
                        : `${fmtNum(p.liveMinutesIncluded)} Live Assist participant-min / mo · hard cap`
                      : "No Live Assist"}</li>
                  </ul>
                  {plan === "enterprise" ? (
                    <a className="btn" href="mailto:hello@backstory.io?subject=Enterprise%20plan">Talk to sales</a>
                  ) : isCurrent && plan === "free" && !state.data.paymentMethodSet && state.data.usageBilling ? (
                    <button className="btn" onClick={() => checkout.mutate("free")} disabled={checkout.isPending}>Add a card for pay-as-you-go</button>
                  ) : isCurrent && state.data.accessDisabled && plan !== "free" ? (
                    <button className="btn primary" onClick={() => checkout.mutate(plan)} disabled={checkout.isPending}>Reactivate {plan}</button>
                  ) : isCurrent ? (
                    <button className="btn" disabled>Your plan</button>
                  ) : (
                    <button className={`btn ${upgrade ? "primary" : ""}`} onClick={() => checkout.mutate(plan)} disabled={checkout.isPending}>
                      {upgrade ? "Upgrade" : "Downgrade"} to {plan}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          <div className="card section" style={{ marginTop: 16 }} data-testid="ai-credit-wallet">
            <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <h2 style={{ marginTop: 0 }}>AI credit wallet</h2>
                <p className="muted small">
                  Your plan includes AI credits each month. When those and this prepaid balance reach zero,
                  platform-funded AI pauses. Session recording and the rest of Backstory keep working.
                </p>
              </div>
              <div style={{ textAlign: "right" }}>
                <div className="muted small">Prepaid balance</div>
                <strong>{creditWallet.isPending ? "…" : fmtUsd(creditWallet.data?.balanceUsd ?? 0)}</strong>
              </div>
            </div>
            <p className="muted small">Add credit before use. Purchased credit carries forward until consumed.</p>
            <div className="row">
              {([25, 50, 100] as const).map((amount) => (
                <BusyButton
                  key={amount}
                  busy={creditCheckout.isPending && creditCheckout.variables === amount}
                  busyLabel={`Opening $${amount} checkout…`}
                  onClick={() => creditCheckout.mutate(amount)}
                  disabled={creditCheckout.isPending}
                >
                  Add ${amount}
                </BusyButton>
              ))}
            </div>
          </div>
          {current !== "free" && !state.data.accessDisabled && !state.data.cancelAt && (
            <div className="card section billing-cancel">
              <h2>Cancel subscription</h2>
              <p className="muted small">Choose when product access should end. Canceling does not create a refund.</p>
              <div className="row">
                <button className="btn" onClick={() => setCancelMode("period_end")}>Cancel at period end</button>
                <button className="btn danger" onClick={() => setCancelMode("immediate")}>Cancel immediately</button>
              </div>
            </div>
          )}
          {state.data.commit && (
            <div className="card section" style={{ marginTop: 16 }} data-testid="commit-card">
              <h2>Enterprise commit</h2>
              <MeterBar label="Annual commit drawn" used={state.data.commit.drawnUsd} included={state.data.commit.totalUsd} unit="USD" />
              <p className="muted small">Period {formatDateTime(state.data.commit.startsAt)} → {formatDateTime(state.data.commit.endsAt)}. Usage draws down against the commit. Anything past it is invoiced monthly and due in 7 days.</p>
            </div>
          )}
        </>
      )}
      <BillingUsage orgId={orgId} fixedCaps={state.data?.usageBilling === false} />
      <div className="card section stack-table" style={{ marginTop: 16 }}>
        <h2>Payments</h2>
        <p className="muted small">View opens the receipt. A payment that paid an invoice opens that invoice.</p>
        {payments.isError ? (
          <p className="muted small">Payments appear once a card is on file.</p>
        ) : (
          <DataTable
            ariaLabel="Payments"
            rows={paymentRows}
            rowKey={(pay) => pay.id}
            loading={payments.isPending}
            loadingMore={payments.isFetchingNextPage}
            empty="No payments yet."
            columns={[
              { key: "date", header: "Date", label: "Date", render: (pay) => formatDateTime(pay.createdAt) },
              { key: "description", header: "Description", label: "Description", render: (pay) => pay.description },
              { key: "status", header: "Status", label: "Status", render: (pay) => <span className={`badge ${pay.status === "paid" ? "ok" : pay.status === "failed" ? "warn" : "neutral"}`}>{paymentStatusLabel(pay.status)}</span> },
              { key: "amount", header: "Amount", label: "Amount", render: (pay) => fmtUsd(pay.amountUsd) },
              { key: "view", header: "", label: "", render: (pay) => pay.viewUrl ? <a className="btn ghost sm" href={pay.viewUrl} target="_blank" rel="noreferrer">View</a> : null },
            ]}
          />
        )}
        <AutoLoadMore hasMore={!!payments.hasNextPage} loading={payments.isFetchingNextPage} loadMore={() => void payments.fetchNextPage()} label="Loading older payments" />
      </div>
      <ConfirmDialog
        open={cancelMode !== null}
        danger
        title={cancelMode === "immediate" ? "End access immediately?" : "Cancel at period end?"}
        confirmLabel={cancel.isPending ? "Canceling…" : cancelMode === "immediate" ? "End access now" : "Confirm cancellation"}
        cancelLabel="Keep subscription"
        busy={cancel.isPending}
        onConfirm={() => cancel.mutate(cancelMode === "immediate")}
        onClose={() => setCancelMode(null)}
      >
        <p>
          {cancelMode === "immediate"
            ? "Recording, product APIs, and SDK uploads will stop now. Your current payment will not be reversed."
            : `Your subscription will not renew. You can keep using Backstory until ${state.data?.currentPeriodEnd ? formatDateTime(state.data.currentPeriodEnd) : "the end of the paid period"}.`}
        </p>
      </ConfirmDialog>
    </OrgLayout>
  );
}

type PaymentNotice = { kind: "credits" | "subscription"; outcome: "success" | "failed" };

function noticeForCheckout(checkout: string | undefined): PaymentNotice | null {
  if (checkout === "ai-credits-success") return { kind: "credits", outcome: "success" };
  if (checkout === "ai-credits-canceled") return { kind: "credits", outcome: "failed" };
  if (checkout === "success") return { kind: "subscription", outcome: "success" };
  if (checkout === "canceled") return { kind: "subscription", outcome: "failed" };
  return null;
}

function paymentStatusLabel(status: string): string {
  switch (status) {
    case "paid": return "Paid";
    case "refunded": return "Refunded";
    case "failed": return "Failed";
    default: return "Pending";
  }
}

function PaymentNotice({
  notice,
  amount,
  onClose,
}: {
  notice: PaymentNotice;
  amount?: number;
  onClose: () => void;
}) {
  const credits = notice.kind === "credits";
  const failed = notice.outcome === "failed";
  const title = failed
    ? credits ? "No credits were added" : "Your plan did not change"
    : credits ? `${amount ? fmtUsd(amount) : "AI"} credits are on the way` : "Your plan is being activated";
  const panel = useRef<HTMLElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    panel.current?.querySelector<HTMLElement>("button")?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCloseRef.current();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      prev?.focus?.();
    };
  }, []);
  return (
    <div className="dialog-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section
        ref={panel}
        className={`payment-success${failed ? " failed" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={failed ? "Payment not completed" : "Payment confirmation"}
      >
        <div className="payment-success-mark" aria-hidden="true">
          <span className="payment-success-ring" />
          <svg viewBox="0 0 24 24" focusable="false">
            {failed ? <path d="m8 8 8 8M16 8l-8 8" /> : <path d="m6.5 12.5 3.4 3.4 7.6-8" />}
          </svg>
          {!failed && <><i /><i /><i /><i /><i /><i /></>}
        </div>
        <div className="payment-success-copy">
          <span className="eyebrow">{failed ? "Payment not completed" : "Payment complete"}</span>
          <h2>{title}</h2>
          <p>
            {failed
              ? "Nothing was charged. You can try the payment again whenever you are ready."
              : credits
                ? "Stripe confirmed the payment. Your balance updates automatically as soon as the credits are applied."
                : "Stripe confirmed the payment. Your subscription and invoice details update automatically."}
          </p>
        </div>
        <button className="btn ghost sm payment-success-close" type="button" onClick={onClose} aria-label={failed ? "Dismiss payment notice" : "Dismiss payment confirmation"}>
          {failed ? "Close" : "Done"}
        </button>
      </section>
    </div>
  );
}

function Countdown({ until, onElapsed }: { until: string; onElapsed?: () => void }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const update = () => {
      tick((n) => n + 1);
      if (new Date(until).getTime() <= Date.now()) onElapsed?.();
    };
    const id = window.setInterval(update, 30_000);
    update();
    return () => window.clearInterval(id);
  }, [onElapsed, until]);
  const remaining = Math.max(0, new Date(until).getTime() - Date.now());
  const days = Math.floor(remaining / 86_400_000);
  const hours = Math.floor((remaining % 86_400_000) / 3_600_000);
  const minutes = Math.floor((remaining % 3_600_000) / 60_000);
  return <strong>{days > 0 ? `${days}d ${hours}h` : `${hours}h ${minutes}m`}</strong>;
}
